import React from 'react'
import { Form, Input, Upload, message, Image } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import type { FormInstance } from 'antd'
import type { UploadRequestOption } from 'rc-upload/lib/interface'
import { supabaseClient } from '../lib/supabaseClient'

interface ImageUploadFieldProps {
  /** The AntD form instance (from useForm's `form`) used to write the uploaded URL. */
  form: FormInstance
  /** The form field name to populate with the public URL (e.g. 'image_url'). */
  name: string
  /** Label for the URL text input. */
  label: string
  /** Tooltip shown on the URL Form.Item. */
  tooltip?: string
  /** Placeholder for the URL text input. */
  placeholder?: string
  /** When set, renders a small <Image> preview of the current URL above the uploader. */
  currentUrl?: string | null
  /** Sub-path under the photos bucket, e.g. 'events' → uploads/events/{file}. */
  folder?: string
}

/**
 * Reusable image field: an AntD <Upload.Dragger> that uploads to the public `photos`
 * Supabase storage bucket and writes the resulting public URL into `name`, PLUS the
 * existing manual URL text Input as an alternative. Admins can either upload or paste.
 */
export function ImageUploadField({
  form,
  name,
  label,
  tooltip,
  placeholder = 'https://…',
  currentUrl,
  folder = 'events',
}: ImageUploadFieldProps) {
  const handleUpload = async (options: UploadRequestOption) => {
    const { file, onSuccess, onError } = options
    const uploadFile = file as File
    const ext = uploadFile.name.split('.').pop() ?? 'jpg'
    const path = `uploads/${folder}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`

    const { error } = await supabaseClient.storage
      .from('photos')
      .upload(path, uploadFile, { upsert: false })

    if (error) {
      message.error(`Upload failed: ${error.message}`)
      onError?.(error)
      return
    }

    const { data: urlData } = supabaseClient.storage.from('photos').getPublicUrl(path)

    const publicUrl = urlData.publicUrl
    form.setFieldValue(name, publicUrl)
    message.success('Image uploaded')
    onSuccess?.(publicUrl)
  }

  return (
    <>
      {currentUrl && (
        <Form.Item label="Current Image">
          <Image
            src={currentUrl}
            alt="current image"
            width={120}
            height={120}
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
        </Form.Item>
      )}

      <Form.Item label="Upload Image">
        <Upload.Dragger
          accept="image/*"
          customRequest={handleUpload}
          maxCount={1}
          showUploadList={{ showRemoveIcon: false }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag image to upload</p>
          <p className="ant-upload-hint">Uploads to Supabase Storage (photos bucket)</p>
        </Upload.Dragger>
      </Form.Item>

      <Form.Item label={label} name={name} tooltip={tooltip}>
        <Input placeholder={placeholder} />
      </Form.Item>
    </>
  )
}
